import { queryParamsToFilterValues } from '@client/routes/fetch/citySearchPage/utils/get-applied-filter';
import type {
	FilterConfig,
	FilterValue,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useCallback, useEffect, useRef } from 'react';

// Module-level ref survives hook remounts — used to reconcile missed popstate events
let lastProcessedUrlGlobal: string | null = null;

// Reset function for testing — clears module-level state between test runs
export const __resetBrowserNavigationSync = () => {
	lastProcessedUrlGlobal = null;
};

/**
 * Custom hook to sync filter state with browser back/forward navigation
 *
 * **Problem this solves:**
 * When users click browser back/forward buttons, we need to update our filter state
 * to match the URL. However, this is complicated by several issues:
 *
 * 1. **Race Condition**: When browser navigation occurs, React Router updates its state
 *    before the `popstate` event fires. If we track the URL reactively (via useEffect
 *    watching pathname/search), the ref gets updated to the new URL before we can compare
 *    it in the popstate handler, causing us to always skip processing.
 *
 * 2. **Route-based Facets**: URLs can contain facets in the pathname (e.g., /affordable, /outdoor)
 *    that need to be parsed as filters. These are extracted dynamically from window.location.pathname.
 *
 * 3. **Stale Closures**: Event listeners capture values at registration time. If filterConfigs,
 *    categoryCode, or setFilterValues change (e.g., during in-app navigation), we need the
 *    listener to use the latest values without re-registering.
 *
 * **Solution:**
 * - Track the last processed URL (without hash) in a ref
 * - Only update this ref in controlled locations:
 *   1. On component mount (initialization)
 *   2. After programmatic URL changes (when filters are applied)
 *   3. Inside the popstate handler (after processing navigation)
 * - DO NOT update it reactively when pathname/search change (avoids race condition)
 * - Extract facet from pathname dynamically to handle URL changes correctly
 * - Use ref pattern to keep handler always up-to-date without re-registering listener
 *
 * **How to use:**
 * ```typescript
 * const { updateLastProcessedUrl } = useBrowserNavigationSync({
 *   filterConfigs,
 *   categoryCode,
 *   setFilterValues,
 *   setBrowserNavigationUrl,
 * });
 *
 * // In your urlFilterParams effect, after calling updateHistory:
 * updateHistory({ ... });
 * updateLastProcessedUrl();
 * ```
 *
 * @param filterConfigs - Available filter configurations
 * @param categoryCode - Current vendor category code
 * @param setFilterValues - Function to update filter state
 * @param setBrowserNavigationUrl - Optional function to publish the back/forward URL to consumers
 * @returns Handler for popstate events and function to update the tracked URL
 */
export const useBrowserNavigationSync = ({
	filterConfigs,
	categoryCode,
	setFilterValues,
	setBrowserNavigationUrl,
}: {
	filterConfigs: Record<string, FilterConfig>;
	categoryCode: Category.CategoryCode;
	setFilterValues: (filters: FilterValue[]) => void;
	setBrowserNavigationUrl?: (url: string) => void;
}) => {
	const lastUrl = useRef<string>('');
	const handlerRef = useRef<(() => void) | null>(null);

	const updateTrackedUrl = useCallback((url: string) => {
		lastUrl.current = url;
		lastProcessedUrlGlobal = url;
	}, []);

	/**
	 * Handler for browser back/forward navigation
	 * Parses URL parameters and updates filter state accordingly
	 */
	const onBrowserNavigation = useCallback(() => {
		const currentUrl = window.location.pathname + window.location.search;

		if (lastUrl.current === currentUrl) {
			return;
		}

		// Update tracked URL and process the navigation
		updateTrackedUrl(currentUrl);

		// Extract facet from current pathname (e.g., /affordable, /outdoor)
		// Format: /marketplace/wedding-venues-chicago-il/affordable
		const pathParts = window.location.pathname.split('/').filter(Boolean);
		const currentFacet = pathParts.length > 2 ? pathParts[2] : null;

		// Parse filters from the current URL (which browser has already updated)
		const urlParams = new URLSearchParams(window.location.search);
		const filters = queryParamsToFilterValues(
			Object.fromEntries(urlParams.entries()),
			currentFacet,
			filterConfigs,
			categoryCode,
		);

		// Publish the exact back/forward URL so FilterPills can skip
		// updateHistory for this one navigation-derived state update.
		if (setBrowserNavigationUrl) {
			setBrowserNavigationUrl(currentUrl);
		}

		setFilterValues(filters);
		// Note: Scroll-to-top is handled by the component's urlFilterParams effect
		// to avoid duplicate scrolling (this update triggers that effect)
	}, [
		setFilterValues,
		filterConfigs,
		categoryCode,
		updateTrackedUrl,
		setBrowserNavigationUrl,
	]);

	// Keep the ref updated with the latest handler
	// This ensures the stable event listener always calls the current version
	useEffect(() => {
		handlerRef.current = onBrowserNavigation;
	}, [onBrowserNavigation]);

	/**
	 * Updates the last processed URL to the current URL
	 * Call this after programmatic URL changes (e.g., after updateHistory)
	 *
	 * IMPORTANT: This must be called AFTER the URL has been updated via history.push,
	 * not in a separate useEffect, to avoid race conditions with popstate events.
	 */
	const updateLastProcessedUrl = useCallback(() => {
		updateTrackedUrl(window.location.pathname + window.location.search);
	}, [updateTrackedUrl]);

	/**
	 * Set up the popstate event listener on mount with a stable wrapper
	 * The wrapper delegates to handlerRef which is always kept up-to-date,
	 * ensuring we always use the latest filterConfigs, categoryCode, and setFilterValues
	 * without needing to re-register the listener
	 */
	useEffect(() => {
		const initialTrackedUrl =
			lastProcessedUrlGlobal ??
			window.location.pathname + window.location.search;
		updateTrackedUrl(initialTrackedUrl);

		const currentUrl = window.location.pathname + window.location.search;
		if (currentUrl !== initialTrackedUrl) {
			handlerRef.current?.();
		}

		// Stable event listener that delegates to the current handler
		const stableListener = () => {
			handlerRef.current?.();
		};

		// Listen for browser navigation (back/forward), not programmatic URL changes
		window.addEventListener('popstate', stableListener);

		return () => {
			window.removeEventListener('popstate', stableListener);
		};
	}, [updateTrackedUrl]);

	return {
		onBrowserNavigation,
		updateLastProcessedUrl,
	};
};
